import { useState } from 'react';
import APIUtils from '../../utils/APIUtils';
import { getConditionKey, parseAnalyteCodeString } from './cutoffUtils';

const getConditionProfile = (condition) =>
  condition.conditionProfile || condition.ConditionProfile || [];

// All analyte codes involved in a single cutoff profile, regardless of type.
const getProfileAnalyteCodes = (profile) => {
  if (!profile) return [];
  if (profile.cutoffType === 'value' || profile.cutoffType === 'SINGLE_ANALYTE') {
    return parseAnalyteCodeString(profile.analyteCode1 || '');
  }
  const numeratorCodes = Array.isArray(profile.ratioNumeratorAnalyteCodes) && profile.ratioNumeratorAnalyteCodes.length
    ? profile.ratioNumeratorAnalyteCodes
    : parseAnalyteCodeString(profile.analyteCode1 || '');
  const denominatorCodes = Array.isArray(profile.ratioDenominatorAnalyteCodes) && profile.ratioDenominatorAnalyteCodes.length
    ? profile.ratioDenominatorAnalyteCodes
    : parseAnalyteCodeString(profile.analyteCode2 || '');
  const sumCodes = Array.isArray(profile.analyteCodes) && profile.analyteCodes.length
    ? profile.analyteCodes
    : parseAnalyteCodeString(profile.analyteCode1 || '');
  if (profile.cutoffType === 'sum') return sumCodes;
  return [...new Set([...numeratorCodes, ...denominatorCodes])];
};

// The import endpoint marks a profile priority 1 when its analyte was flagged
// isPrimary in the file, but it does NOT add that analyte code to the
// condition's primaryAnalyte string. Reconcile here so any priority-1 profile's
// analyte code(s) are reflected in primaryAnalyte — otherwise the cutoff table's
// Primary Analytes column omits the imported primary analyte.
const reconcilePrimaryAnalyte = (condition) => {
  const profiles = getConditionProfile(condition);
  if (!Array.isArray(profiles) || profiles.length === 0) return condition;

  const primaryKey = condition.primaryAnalyte !== undefined ? 'primaryAnalyte'
    : (condition.PrimaryAnalyte !== undefined ? 'PrimaryAnalyte' : 'primaryAnalyte');
  const existingPrimary = parseAnalyteCodeString(condition.primaryAnalyte || condition.PrimaryAnalyte || '');
  const primarySet = new Set(existingPrimary);
  const orderedPrimary = [...existingPrimary];

  profiles.forEach((profile) => {
    if (Number(profile?.priority) !== 1) return;
    getProfileAnalyteCodes(profile).forEach((code) => {
      if (code && !primarySet.has(code)) {
        primarySet.add(code);
        orderedPrimary.push(code);
      }
    });
  });

  if (orderedPrimary.length === existingPrimary.length) return condition;
  return { ...condition, [primaryKey]: orderedPrimary.join(',') };
};

// Conditions whose cutoff count increased between the current set and the
// validated set — i.e. the cutoffs the import would actually add.
const getAddedConditions = (before, after) => {
  const beforeCountByKey = new Map();
  (Array.isArray(before) ? before : []).forEach((condition) => {
    beforeCountByKey.set(getConditionKey(condition), getConditionProfile(condition).length);
  });
  return (Array.isArray(after) ? after : []).filter((condition) => {
    const priorCount = beforeCountByKey.get(getConditionKey(condition)) || 0;
    return getConditionProfile(condition).length > priorCount;
  });
};

// Stable signature of a cutoff profile's meaningful fields, used to tell which
// profiles are newly added by an import (independent of array order and of any
// UI-only fields that may be present on one side but not the other).
const getProfileSignature = (profile) => JSON.stringify({
  cutoffType: profile?.cutoffType ?? null,
  analyteCode1: profile?.analyteCode1 ?? null,
  analyteCode2: profile?.analyteCode2 ?? null,
  normalRange: profile?.normalRange ?? null,
  birthWeight: profile?.birthWeight ?? null,
  collectionAge: profile?.collectionAge ?? null,
  gestationAge: profile?.gestationAge ?? null,
  flagMessage: profile?.flagMessage ?? null,
});

// Flag the profiles that an import newly adds so the table can highlight them as
// unsaved. Compares each condition's post-import profiles against the profile
// signatures it had before; any profile not previously present is marked with
// `isPendingSave: true`. Returns both the marked set and the keys of the
// conditions that gained profiles (so those rows can be auto-expanded).
const markPendingImportProfiles = (before, after) => {
  const beforeSignaturesByKey = new Map();
  (Array.isArray(before) ? before : []).forEach((condition) => {
    beforeSignaturesByKey.set(
      getConditionKey(condition),
      new Set(getConditionProfile(condition).map(getProfileSignature)),
    );
  });

  const expandedKeys = [];
  const marked = (Array.isArray(after) ? after : []).map((condition) => {
    const priorSignatures = beforeSignaturesByKey.get(getConditionKey(condition)) || new Set();
    const profiles = getConditionProfile(condition);
    let conditionHasNewProfile = false;

    const nextProfiles = profiles.map((profile) => {
      const isNew = !priorSignatures.has(getProfileSignature(profile));
      if (!isNew) return profile;
      conditionHasNewProfile = true;
      return { ...profile, isPendingSave: true };
    });

    if (!conditionHasNewProfile) return condition;

    const profileKey = condition.conditionProfile !== undefined ? 'conditionProfile' : 'ConditionProfile';
    expandedKeys.push(String(condition.orphanetCode || condition.OrphanetCode || condition.name || condition.Name || ''));
    return { ...condition, [profileKey]: nextProfiles };
  });

  return { marked, expandedKeys };
};

// Encapsulates the "Import Cutoffs" dialog flow: open/close state, the validate
// request, and the result dialogs. Import now only VALIDATES — the validated
// cutoff set becomes pending/unsaved table state and the user must click Apply
// to persist it. Cutoff value setters are injected because a validated import
// replaces the manual cutoff set and may clear temp condition rows that now
// have profile data.
const useCutoffImport = ({
  programId,
  cutoffValues,
  enabledConditions,
  setCutoffValues,
  setTempConditionRows,
  setExpandedCutoffRows,
  setBiochemicalError,
}) => {
  const [importCutoffsDialogOpen, setImportCutoffsDialogOpen] = useState(false);
  const [importCutoffsImporting, setImportCutoffsImporting] = useState(false);
  const [importCutoffsValidatedOpen, setImportCutoffsValidatedOpen] = useState(false);
  const [importCutoffsNoChangesOpen, setImportCutoffsNoChangesOpen] = useState(false);
  const [importCutoffsErrorOpen, setImportCutoffsErrorOpen] = useState(false);
  const [importCutoffsErrorMessages, setImportCutoffsErrorMessages] = useState([]);
  const [pendingImportConditions, setPendingImportConditions] = useState([]);
  // Number of individual cutoffs staged by the most recent validated import,
  // surfaced in the save snackbar once the user clicks Apply.
  const [importedCutoffCount, setImportedCutoffCount] = useState(0);

  const handleOpenImportCutoffsDialog = () => {
    setImportCutoffsDialogOpen(true);
  };

  const handleCloseImportCutoffsDialog = () => {
    if (importCutoffsImporting) return;
    setImportCutoffsDialogOpen(false);
  };

  const handleImportCutoffs = async (file) => {
    setImportCutoffsImporting(true);
    setBiochemicalError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await APIUtils.validateCutoffImport(programId, formData);
      if (result.isValid) {
        // The import endpoint returns the fully-merged, de-duplicated cutoff set
        // (existing cutoffs + newly imported cutoffs). We do NOT persist here —
        // the validated set becomes pending/unsaved table state and the user must
        // click Apply to save it.
        const validatedCutoffValuesRaw = Array.isArray(result.importCutOff) ? result.importCutOff : [];

        // Reconcile the `enabled` flag against the program's current enabled
        // conditions. The import endpoint can return a condition with
        // `enabled: false` even though it is currently enabled for the program,
        // which would otherwise cause the row to be hidden from the cutoff table
        // (and excluded from the Add Condition dialog because it has profile
        // data), making an imported condition silently disappear.
        const enabledSet = new Set(Array.isArray(enabledConditions) ? enabledConditions : []);
        const validatedCutoffValues = validatedCutoffValuesRaw.map((condition) => {
          const acronym = condition.acronym || condition.Acronym;
          const enabledCorrected = (acronym && enabledSet.has(acronym) && condition.enabled === false)
            ? { ...condition, enabled: true }
            : condition;
          return reconcilePrimaryAnalyte(enabledCorrected);
        });

        const addedConditions = getAddedConditions(cutoffValues, validatedCutoffValues);
        setImportCutoffsDialogOpen(false);

        if (addedConditions.length === 0) {
          // Nothing new — every cutoff in the file already exists. Leave state
          // untouched so the tab stays clean and Apply remains disabled.
          setPendingImportConditions([]);
          setImportedCutoffCount(0);
          setImportCutoffsNoChangesOpen(true);
          return;
        }

        // Load the validated set into the table as unsaved changes.
        // initialCutoffValues is intentionally left unchanged so the tab becomes
        // dirty and the Apply button enables.
        const { marked, expandedKeys } = markPendingImportProfiles(cutoffValues, validatedCutoffValues);
        // Count the individual cutoffs this import newly staged (the server
        // result carries no isPendingSave flag, so every flagged profile is new).
        const newlyImportedCount = marked.reduce(
          (sum, condition) => sum + getConditionProfile(condition).filter((p) => p && p.isPendingSave).length,
          0,
        );
        setImportedCutoffCount(newlyImportedCount);
        setCutoffValues(marked);
        setTempConditionRows(prev => prev.filter(tc => {
          const tcKey = getConditionKey(tc);
          return !validatedCutoffValues.some(c => getConditionKey(c) === tcKey &&
            getConditionProfile(c).length > 0);
        }));
        // Auto-expand the condition rows that gained imported cutoffs so the
        // highlighted, not-yet-saved subrows are immediately visible.
        setExpandedCutoffRows(prev => {
          const next = { ...prev };
          expandedKeys.forEach((key) => { if (key) next[key] = true; });
          return next;
        });
        setPendingImportConditions(addedConditions);
        setImportCutoffsValidatedOpen(true);
      } else {
        setImportCutoffsErrorMessages(result.messages || ['Unknown validation error.']);
        setImportCutoffsDialogOpen(false);
        setImportCutoffsErrorOpen(true);
      }
    } catch (err) {
      console.error('Error importing cutoffs:', err);
      const data = err.response?.data;
      let messages;
      if (data?.messages && Array.isArray(data.messages)) {
        messages = data.messages;
      } else if (data?.message) {
        messages = [data.message];
      } else if (typeof data === 'string') {
        messages = [data];
      } else {
        messages = ['An error occurred during cutoff import. Please try again.'];
      }
      setImportCutoffsErrorMessages(messages);
      setImportCutoffsDialogOpen(false);
      setImportCutoffsErrorOpen(true);
    } finally {
      setImportCutoffsImporting(false);
    }
  };

  return {
    importCutoffsDialogOpen,
    importCutoffsImporting,
    importCutoffsValidatedOpen,
    importCutoffsNoChangesOpen,
    importCutoffsErrorOpen,
    importCutoffsErrorMessages,
    pendingImportConditions,
    importedCutoffCount,
    setImportCutoffsValidatedOpen,
    setImportCutoffsNoChangesOpen,
    setImportCutoffsErrorOpen,
    setPendingImportConditions,
    setImportedCutoffCount,
    handleOpenImportCutoffsDialog,
    handleCloseImportCutoffsDialog,
    handleImportCutoffs,
  };
};

export default useCutoffImport;
